"use client";

import React from "react";
import { Activity, Brain, Waves, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const clinicalStats = [{
  id: "stat-rem",
  value: "30M+",
  label: "Adults in the US",
  description: "Approximately 30 million Americans are affected by sleep apnea, a condition that disrupts breathing and prevents restorative rest during the night.",
  progress: 75,
  icon: Activity
}, {
  id: "stat-onset",
  value: "3x",
  label: "Stroke Risk Increase",
  description: "Clinical studies show that individuals with untreated obstructive sleep apnea face a three-fold increase in the risk of suffering a major stroke.",
  progress: 66,
  icon: Brain
}, {
  id: "stat-alignment",
  value: "80%",
  label: "Remain Undiagnosed",
  description: "An alarming 80 percent of moderate to severe sleep apnea cases go undiagnosed, leaving millions at risk for serious long-term health complications.",
  progress: 80,
  icon: Waves
}];
export default function TheScienceOfBetterRest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="173448" id="vital-statistics" className="dark bg-background py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        <div className="mb-16 md:mb-24 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-mono text-primary uppercase tracking-widest mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
            Public Health Data
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold tracking-tight text-foreground mb-6">
            The Impact of Sleep Apnea.
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl">
            Sleep apnea is more than just snoring; it is a serious medical condition that affects millions. Understanding the statistics is the first step toward seeking life-saving treatment.
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {clinicalStats.map((stat, i) => <Card key={stat.id} data-index={i} className={`bg-card/50 border-border/60 backdrop-blur-md transition-all duration-1000 ease-out flex flex-col justify-between shadow-lg ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${i * 200}ms`
        }}>
              <CardHeader className="pb-8">
                <div className="mb-8 p-4 rounded-2xl bg-primary/5 border border-primary/10 inline-flex self-start">
                  <stat.icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                </div>
                <CardTitle className="text-6xl lg:text-7xl font-sans font-bold tracking-tighter text-primary mb-2">
                  {stat.value}
                </CardTitle>
                <CardDescription className="text-lg font-semibold text-foreground tracking-tight">
                  {stat.label}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground mb-8 leading-relaxed font-sans">
                  {stat.description}
                </p>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs font-mono text-muted-foreground uppercase tracking-wider">
                    <span>Low Risk</span>
                    <span className="text-primary font-bold">High Prevalence</span>
                  </div>
                  <Progress value={stat.progress} className="h-2 bg-muted border border-border/30" />
                </div>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-16 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-6 border-t border-border/20 pt-12">
          <p className="text-sm text-muted-foreground font-sans max-w-md">
            *Source: American Sleep Apnea Association and National Institutes of Health. Statistics reflect current clinical research and diagnostic trends.
          </p>
          <Button asChild size="lg" className="rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground px-8 group transition-all">
            <Link to="/HealthRisksPage">
              Explore Health Risks
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}